import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  Box,
  ChangeRequestDialog,
  ContentTemplate,
  Grid,
  PAYLOAD_DATE_FORMAT,
  Progress,
  SectionHeader,
  TitleHeader,
  tomorrowDate,
  useSnackbarHandler,
  stringToSemanticDisplay,
} from 'web-library';
import { useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useParams } from 'react-router-dom';
import { FunctionModule } from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import EditButton from 'web-library/components/buttons/EditButton';
import {
  AccountManagementActionTypesResponseObject,
  AdminProfileResponse,
} from 'web-library/models/AccountManagementConfiguration';
import { AUTHORITY_NAMES, FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { MutationStatus } from 'react-query';
import dayjs from 'dayjs';
import { usePostSearchApprovers } from '../../../api-refactored/user-group-access-configuration/usePostSearchApprovers';
import { useGetAdminProfile } from '../../../api-refactored/account-management/useGetAdminProfile';
import AdminPersonalParticulars from './AdminPersonalParticulars';
import AdminContactInformation from './AdminContactInformation';
import { usePutUpdateAdminProfile } from '../../../api-refactored/account-management/usePutUpdateAdminProfile';
import { usePostCreateAdminProfile } from '../../../api-refactored/account-management/usePostCreateAdminProfile';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import { IExamContentResponse } from '../../../api/user/model';
import { usePostAccountManagementAction } from '../../../api-refactored/account-management/usePostAccountManagementAction';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import { usePostRequestFilePresigned } from '../../../api-refactored/user-group-access-configuration/usePostRequestFilePresigned';
import CreateEditAdminSchema, { CreateEditAdminSchemaData } from './CreateEditAdminSchema';
import ManageAccountContent from './ManageAccountContent';
import ActionApproverRemarksContent from './ActionApproverRemarksContent';
import AccountManagementMiscellaneousComponents from './AccountManagementMiscellaneousComponents';
import {
  useCreateEditViewAdminAccountDetails,
  useEditViewAdminFormValues,
} from './useCreateEditViewAdminAccountDetails';
import ToggleDisplayEditFormItem from '../../../components/ToggleDisplayEditFormItem';
import ControlledDatePicker from 'web-library/stories/atom/DatePicker/ControlledDatePicker';

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) => ({
  editParticularsButton: {
    marginLeft: theme.spacing(3),
  },
  formHeader: {
    marginTop: theme.spacing(3),
  },
  halfWidthLeftItem: {
    width: '50%',
    paddingRight: theme.spacing(2),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
  halfWidthRightItem: {
    width: '50%',
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: 0,
      marginBottom: theme.spacing(2),
    },
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
    width: '100%',
  },
  formElementMarginBottom: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  responsiveFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  statusContainer: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    height: 0,
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
    borderWidth: 0,
    borderTopWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.color.border.borderAlt,
  },
  buttonContainer: {
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(0),
    },
  },
  backButton: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  formButtonContainer: {
    display: 'flex',
    marginBottom: theme.spacing(7),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  actionApprover: {
    marginBottom: theme.spacing(4),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  fileDropZoneContainer: {
    paddingTop: theme.spacing(6),
  },
}));

export interface CreateEditViewAdminAccountDetailsProps {
  isEdit: boolean;
  isCreate: boolean;
}

const CreateEditViewAdminAccountDetails = ({
  isEdit,
  isCreate,
}: CreateEditViewAdminAccountDetailsProps) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();

  const { uuid } = useParams<{ uuid: string }>();
  const [isEditState, setIsEditState] = useState<boolean>(!isEdit);
  const [isApproverNeededAction, setIsApproverNeededAction] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const [checkDisable, setcheckDisable] = useState<boolean>(false);

  const [filteredActionTypes, setFilteredActionTypes] = useState<
    AccountManagementActionTypesResponseObject[]
  >([]);

  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');

  const [files, setFiles] = useState<UploadedFile[]>([]);

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState<string>('');

  const [openConfirmChangesModal, setOpenConfirmChangesModal] = useState<boolean>(false);

  const [deleteAccountModalMessage, setDeleteAccountModalMessage] = useState<string>('');

  const { data: adminProfileDetails, refetch: refetchAdminProfileDetails } = useGetAdminProfile({
    uuid,
    enabled: !!uuid && isEdit,
    onSuccess: () => {
      setFiles([]);
      setErrorMessage('');
      setUploadFileErrorMessage('');
      refetchAccountManagementActionTypes();
    },
  });
  const [expiryDate, setExpiryDate] = useState<string>(
    adminProfileDetails?.adminModel.expiryDate || ''
  );
  const { data: authorities } = useGetUserAuthorities();

  const {
    mutate: mutatePostSearchApprovers,
    data: postSearchApproversData,
  } = usePostSearchApprovers({});

  const hasEditPermission = authorities?.includes(AUTHORITY_NAMES.CP_ADMIN_USER_ACCOUNTS_EDIT);

  const { data: iExamMasterCode } = useGetIExamMasterCode();

  const { mutate: mutatePostAccountManagementAction } = usePostAccountManagementAction({
    onSuccess: () => {
      setApiButtonStatus('success');
      setShouldSubmit(false);
      handleBackToAdminListingWithAlert();
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.message!);
      setShouldSubmit(false);
      setApiButtonStatus('error');
    },
  });

  const { disabledFields, formDefaultValues } = useEditViewAdminFormValues({
    iExamMasterCode: iExamMasterCode as IExamContentResponse,
    adminProfileDetails: adminProfileDetails as AdminProfileResponse,
    isEditState,
    isApproverNeededAction,
    isEdit,
  });
  const formHook = useForm<CreateEditAdminSchemaData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      CreateEditAdminSchema(isEdit || isEditState ? disabledFields : undefined)
    ),
    defaultValues: formDefaultValues,
  });

  const { clearErrors, handleSubmit, getValues, setValue, watch, control } = formHook;

  const watchAccountChange = watch('accountType');
  const watchActionType = watch('action');

  const { mutate: mutatePutUpdateAdminProfile } = usePutUpdateAdminProfile({
    uuid,
    onSuccess: () => {
      setApiButtonStatus('success');
      setShouldSubmit(false);
      setIsEditState(false);
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserAccountManagement.ActionRequestMessage', {
          action: 'Edit particulars',
        }),
      });
      refetchAdminProfileDetails().then(() => null);
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.message!);
      setApiButtonStatus('error');
    },
  });

  const { mutate: mutatePostCreateAdminProfile } = usePostCreateAdminProfile({
    onSuccess: () => {
      setErrorMessage('');
      setApiButtonStatus('success');
      handleBackToAdminListing();
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.message!);
      setApiButtonStatus('error');
    },
  });

  const { mutate: postRequestFilePresigned } = usePostRequestFilePresigned({
    onSuccess: (data) => {
      const tempFiles: UploadedFile[] = [];
      data.forEach((_value, index) => {
        const pendingFile = files[index];
        const fileData = data[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        tempFiles.push(pendingFile);
      });
      setFiles(tempFiles);
    },
    onError: (err) => {
      setUploadFileErrorMessage(err?.response?.data.message!);
      setApiButtonStatus('error');
    },
  });

  const {
    handleConfirmChangesButton,
    handleEditParticulars,
    handleConfirmChanges,
    handleBackToAdminListing,
    handleBackToAdminListingWithAlert,
    refetchAccountManagementActionTypes,
  } = useCreateEditViewAdminAccountDetails({
    formHook,
    adminProfileDetails: adminProfileDetails as AdminProfileResponse,
    setDeleteAccountModalMessage,
    isApproverNeededAction,
    isEditState,
    setIsEditState,
    setOpenConfirmChangesModal,
    setShouldSubmit,
    files,
    setFiles,
    setApiButtonStatus,
    setUploadFileErrorMessage,
    formDefaultValues,
    uploadFileErrorMessage,
    postRequestFilePresigned,
    uuid,
    setFilteredActionTypes,
  });

  const handleCreateUpdateAdminProfile = useCallback(
    (fileKeys: string[]) => {
      if (!isEditState && shouldSubmit) {
        const AccountManagementActionPayload = {
          actionType: getValues('action')?.code,
          payload: {
            request: isApproverNeededAction
              ? {
                  requestorRemarks: getValues('remarks'),
                  approverUuid: getValues('approver')?.code,
                  fileKeys,
                }
              : null,
            userUuid: uuid,
            userType: 'ADMINISTRATOR',
          },
        };
        mutatePostAccountManagementAction(AccountManagementActionPayload);
      } else if (isEdit) {
        const updateAdminProfilePayload = {
          adminModel: {
            name: getValues('name'),
            email: getValues('email'),
            dateOfBirth: getValues('dateOfBirth')
              ? dayjs(getValues('dateOfBirth')).format(PAYLOAD_DATE_FORMAT)
              : adminProfileDetails?.adminModel.dateOfBirth,
            mobileNumberPrefix: '+',
            mobileNumberCountryCode: getValues('mobileCountryCode')?.codeValue,
            mobileNumber: getValues('mobileContactNumber'),
            roles: adminProfileDetails?.adminModel.roles,
            username: adminProfileDetails?.adminModel.username || '',
            adminId: getValues('nricFinPassport') || null,
            credentialType: getValues('accountType'),
          },
          requestCreationModel: {
            requestorRemarks: getValues('remarks'),
            approverUuid: getValues('approver')?.code,
            fileKeys,
          },
        } as any;
        mutatePutUpdateAdminProfile(updateAdminProfilePayload);
      } else if (!isEdit) {
        const createAdminProfilePayload = {
          adminModel: {
            name: getValues('name'),
            email: getValues('email'),
            dateOfBirth: dayjs(getValues('dateOfBirth')).format(PAYLOAD_DATE_FORMAT),
            mobileNumberCountryCode: getValues('mobileCountryCode')?.codeValue,
            mobileNumber: getValues('mobileContactNumber'),
            roles: getValues('userGroup'),
            adminId: getValues('nricFinPassport'),
            credentialType: getValues('accountType'),
            expiryDate: getValues('expiryDate'),
          },
          requestCreationModel: {
            requestorRemarks: getValues('remarks'),
            approverUuid: getValues('approver')?.code,
            fileKeys,
          },
        };
        mutatePostCreateAdminProfile(createAdminProfilePayload);
      }
    },
    [
      adminProfileDetails?.adminModel.dateOfBirth,
      adminProfileDetails?.adminModel.roles,
      adminProfileDetails?.adminModel.username,
      getValues,
      isApproverNeededAction,
      isEdit,
      isEditState,
      mutatePostAccountManagementAction,
      mutatePostCreateAdminProfile,
      mutatePutUpdateAdminProfile,
      shouldSubmit,
      uuid,
    ]
  );

  useEffect(() => {
    mutatePostSearchApprovers({
      functionName: FUNCTION_NAMES.USER_ACCOUNTS,
      functionModule: FunctionModule.ADMIN,
    });
  }, [mutatePostSearchApprovers]);

  useEffect(() => {
    if (watchAccountChange && watchAccountChange !== 'SINGPASS') {
      setcheckDisable(true);
      setValue('nricFinPassport', '');
      clearErrors('nricFinPassport');
    } else {
      setcheckDisable(false);
    }
  }, [clearErrors, setValue, watchAccountChange]);

  useEffect(() => {
    if (
      watchActionType?.code === 'RESEND_ACTIVATION_EMAIL' ||
      watchActionType?.code === 'RESET_PASSWORD' ||
      !watchActionType
    ) {
      setIsApproverNeededAction(false);
      setUploadFileErrorMessage('');
    } else {
      setIsApproverNeededAction(true);
    }
  }, [watchActionType, watchActionType?.code]);

  useEffect(() => {
    const isAllFilesUploaded = files.every((f) => f.uploaded);
    const fileUploadHasError = files.every((f) => f.hasError);
    const fileKeys: string[] = [''];
    files.forEach((f) => {
      fileKeys.push(f.key as string);
    });

    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }
    if (isAllFilesUploaded && shouldSubmit) {
      handleCreateUpdateAdminProfile(fileKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSubmit, files]);

  useEffect(() => {
    if (files.length) {
      setUploadFileErrorMessage('');
    }
  }, [files.length, setUploadFileErrorMessage]);

  if (adminProfileDetails === undefined && isEdit) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <form onSubmit={handleSubmit(handleConfirmChangesButton)}>
      <TitleHeader
        title={t(
          isEdit
            ? 'admin->>UserAccountManagement.ViewDetails'
            : 'admin->>UserAccountManagement.CreateAdminAccount'
        )}
      />
      <ContentTemplate type="full">
        <SectionHeader title={t('admin->>UserAccountManagement.AccountDetails')}>
          <>
            {hasEditPermission && !isEditState && (
              <Box className={classes.editParticularsButton}>
                <EditButton
                  editHandler={handleEditParticulars}
                  label={t('admin->>UserAccountManagement.EditParticulars')}
                  typographyVariant="h5"
                />
              </Box>
            )}
          </>
        </SectionHeader>
        <>
          <Accordion
            defaultExpanded
            header={t('admin->>UserAccountManagement.PersonalParticulars')}
          >
            <AdminPersonalParticulars
              isEditState={isEditState}
              formHook={formHook}
              adminProfileDetails={adminProfileDetails?.adminModel}
              checkDisable={checkDisable}
            />
          </Accordion>

          <Accordion defaultExpanded header={t('admin->>UserAccountManagement.ContactInformation')}>
            <AdminContactInformation
              isEditState={isEditState}
              formHook={formHook}
              adminProfileDetails={adminProfileDetails?.adminModel}
            />
          </Accordion>
        </>
        {!isEditState && (
          <ManageAccountContent
            userType={adminProfileDetails?.adminModel.userType as string}
            accountType={adminProfileDetails?.adminModel.accountType as string}
            activeStatus={adminProfileDetails?.adminModel.activeStatus as string}
            lockStatus={adminProfileDetails?.adminModel.lockStatus as string}
            blockStatus={adminProfileDetails?.adminModel.blockStatus as string}
            expiredStatus={adminProfileDetails?.adminModel.expiredStatus as string}
            roles={adminProfileDetails?.adminModel.roles}
          />
        )}
        <Box className={classes.responsiveFlexContainer}>
          <Grid
            container
            className={[classes.formElementContainer, classes.formElementMarginBottom].join(' ')}
          >
            <Grid md={5} sm={12} xs={12}>
              <ToggleDisplayEditFormItem
                isEditState={isEditState}
                labelText={t('admin->>CreateOpsAdminForm.ExpirationDate')}
                valueText={
                  adminProfileDetails?.adminModel?.expiryDate !== undefined
                    ? stringToSemanticDisplay(adminProfileDetails?.adminModel?.expiryDate)
                    : '-'
                }
                containerClassName={classes.formElementContainer}
                EditComponent={
                  <ControlledDatePicker
                    value={expiryDate}
                    control={control}
                    label={t('admin->>CreateOpsAdminForm.ExpirationDate')}
                    onChange={(date) => {
                      setExpiryDate(date || '');
                      formHook?.setValue('expiryDate', date || '');
                    }}
                    inputVariant="filled"
                    error={!!formHook?.formState?.errors?.expiryDate}
                    helperText={formHook?.formState?.errors?.expiryDate?.message?.toString()}
                    minDate={dayjs(tomorrowDate)}
                    name="expiryDate"
                    disabled={!isCreate}
                  />
                }
              />
            </Grid>
          </Grid>
        </Box>
        <ActionApproverRemarksContent
          filteredActionTypes={filteredActionTypes}
          apiButtonStatus={apiButtonStatus}
          postSearchApproversData={postSearchApproversData}
          files={files}
          setFiles={setFiles}
          isEditState={isEditState}
          isApproverNeededAction={isApproverNeededAction}
          formHook={formHook}
          isEdit={isEdit}
          isAdmin
        />
        <AccountManagementMiscellaneousComponents
          errorMessage={errorMessage}
          uploadFileErrorMessage={uploadFileErrorMessage}
          apiButtonStatus={apiButtonStatus}
          authorities={authorities}
          setFiles={setFiles}
          isEdit={isEdit}
          isEditState={isEditState}
          setUploadFileErrorMessage={setUploadFileErrorMessage}
          setErrorMessage={setErrorMessage}
          setIsEditState={setIsEditState}
          handleBackToListing={handleBackToAdminListing}
          profileDetails={adminProfileDetails}
          deleteAccountModalMessage={deleteAccountModalMessage}
          setDeleteAccountModalMessage={setDeleteAccountModalMessage}
          setOpenConfirmChangesModal={setOpenConfirmChangesModal}
        />
        <ChangeRequestDialog
          open={openConfirmChangesModal}
          onClose={() => setOpenConfirmChangesModal(false)}
          onConfirm={handleConfirmChanges}
        />
      </ContentTemplate>
    </form>
  );
};

export default CreateEditViewAdminAccountDetails;
